import {Card, DataStatus} from 'platform/components';
import {Text} from 'platform/foundation';

import {useGetCheckoutV2Query, usePatchSellerAsSupplierMutation} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

interface SellerAsSupplierToggleProps extends TestIdProps {
  checkoutId: string;
  isReadonly?: boolean;
}

export function SellerAsSupplierToggle(props: SellerAsSupplierToggleProps) {
  const {
    data: checkout,
    isLoading: isCheckoutLoading,
    isFetching: isCheckoutFetching,
    isError: isCheckoutError,
    refetch: refetchCheckout,
  } = useGetCheckoutV2Query({checkoutId: props.checkoutId});
  const [patchSellerAsSupplier, {isLoading: isPatchingSellerAsSupplier}] =
    usePatchSellerAsSupplierMutation();

  // A rejected change leaves the server state untouched, so the mutation does not invalidate the
  // checkout. The exception is a rejection caused by someone else changing it first — most of all
  // issuing the document, which locks the toggle — so a failure reloads it explicitly.
  const handleToggle = (enabled: boolean) =>
    patchSellerAsSupplier({
      checkoutId: props.checkoutId,
      sellerAsSupplierRequestBody: {enabled},
    })
      .unwrap()
      .catch((error: ApiError) => {
        handleApiError(error);
        refetchCheckout();
      });

  const isLocked = checkout?.sellerAsSupplierToggleLocked ?? false;

  return (
    <DataStatus isLoading={isCheckoutLoading} isError={isCheckoutError}>
      <Card
        variant="inlineGrey"
        title={i18n.t('general.labels.sellerAsSupplier')}
        control={{
          type: 'switch',
          onChange: handleToggle,
          value: checkout?.sellerAsSupplierEnabled ?? false,
          isDisabled:
            isLocked ||
            isCheckoutFetching ||
            isPatchingSellerAsSupplier ||
            (props.isReadonly ?? false),
          isLoading: isPatchingSellerAsSupplier,
          tooltip: isLocked
            ? i18n.t('entity.checkout.labels.sellerAsSupplierToggleLockedTooltip')
            : undefined,
        }}
        data-testid={suffixTestId('sellerAsSupplier', props)}
      >
        <Text size="small" color="secondary">
          {i18n.t('entity.checkout.labels.sellerAsSupplierToggleDescription')}
        </Text>
      </Card>
    </DataStatus>
  );
}
